import { getParent } from 'mobx-state-tree'
import dynamic from 'next/dynamic'
import TRACKING from '~/services/tracking'
import * as StringTools from '~/utilities/string'
import * as Delays from '~/utilities/delays'
import BasicLoader from '~/components/traffic-tickets/screens/basicLoader'
import {PAYMENT_PROVIDERS, REQUEST_STATUSES} from "~/utilities/constants"
import {getStepFromSlug} from "~/components/traffic-tickets/steps/steps"

async function persistTrafficTickets(self) {
  try {
    const TRAFFIC_TICKET_KEYS_TO_SAVE = [
      // 'ticketID',
      'comment',
      'locationState',
      'trafficRegion',
      'violationName',
      'points'
    ]

    const ticketSavePromises = self.trafficTickets.map(ticket => {
      if (ticket.isPersisted) {
        return ticket.updateTicket(TRAFFIC_TICKET_KEYS_TO_SAVE)
      }
      return ticket
          .createTicket(TRAFFIC_TICKET_KEYS_TO_SAVE, 'submission')
          .then(() => {
            TRACKING.traffic.firstPersist()
          })
    })

    await Promise.all(ticketSavePromises)
    return true
  } catch (err) {
    self.changeStatusText(null)
    self.showErrorToast(`We could not update your ticket. Please try again.`)
    if (process.env.WINIT_ENV !== 'test') {
      console.error('Error updating ticket', err)
    }
    return false
  }
}

const USER_INFO_CONFIGURATION = {
  slug: 'user-info',
  Component: dynamic(() => import('./index'), {
    loading: BasicLoader
    // ssr: false
  }),
  validateKeys: ['name', 'email', 'phoneNumber'],
  asyncValidateKeys: ['phoneNumber'],
  validatationTarget: self => {
    const rootStore = getParent(self)
    return rootStore.auth.user
  },
  // eslint-disable-next-line
  onEnter: async self => {
    self.setIsLoading(false)
    const { auth } = getParent(self)

    if (auth.isLoggedIn) {
      const isTicketCreated = await persistTrafficTickets(self)
      if (isTicketCreated) {
        if (
            (!self.trafficRegionsLoadedAll) &&
            (self.trafficRegionsStatus !== REQUEST_STATUSES.loading) &&
            (!self.trafficRegions.length)
        ) {
          try {
            await self.loadRegions()
          } catch (error) {
            if (process.env.WINIT_ENV !== 'test') {
              console.error('error fetching traffic regions for state', error)
            }
            const all = getParent(self)
            all.ui.toasts.addToast({
              message: `Issue fetching traffic regions: ${error}`,
              type: 'ERROR'
            })
          }
        }
        self.setStep(getStepFromSlug('region-court'))
      }
    }
  },
  // eslint-disable-next-line
  onExit: async self => {
    let allStores = getParent(self)
    let authStore = allStores.auth
    let userStore = authStore.user
    const paymentStore = allStores.payment
    const trafficSubmissionStore = allStores.trafficSubmission

    if (!paymentStore.agreedToTermsOfService) {
      paymentStore.setTosError()
      return false
    }

    const USER_KEYS_TO_SAVE = ['email', 'username', 'name', 'phoneNumber']
    try {
      // Should we register the user or are they already logged in.
      if (!authStore.isLoggedIn) {
        // register user

        // set temporary random password
        const temporaryPassword = StringTools.random(15)
        userStore.changeKey('password', temporaryPassword)
        // set payment provider to braintree, backend will generate customer id
        userStore.changeKey('paymentProcessor', {
          type: PAYMENT_PROVIDERS.cko
        })

        // Save (createdUser: bool)
        const createdUser = await userStore.createNewUser({
          keys: [...USER_KEYS_TO_SAVE, 'password', 'paymentProcessor'],
          locationState: trafficSubmissionStore.selectedState?.shortName
        })

        // If we created the user, login as that user
        if (createdUser) {
          TRACKING.general.auth.signup()
          // DELAY - Parse needs to generate payment customer in after save...
          await Delays.msDelayPromise(2000)
          // Login to get Session token & full user info
          authStore.changeKey('email', userStore.email.toLowerCase().trim())
          authStore.changeKey('password', userStore.password)
          const wasLoginSuccessful = await authStore.login()
          // needed since objects will be remounted
          allStores = getParent(self)
          authStore = allStores.auth
          userStore = authStore.user
          // if we saved no longer need a password
          if (wasLoginSuccessful) {
            userStore.changeKey('accountCreatedWithRandomPassword', true)
            userStore.changeKey('password', '')
            userStore.removeChangesForKey('password')
          }
        } else {
          // we failed to create a new user
          userStore.removeChangesForKey('password')
          return false
        }
      } else {
        // persist user fields
        const wasUpdated = await userStore.updateUser(USER_KEYS_TO_SAVE)
        if (!wasUpdated) throw new Error('Issue updating user.')
      }
    } catch (err) {
      self.showErrorToast(
        `Sorry we could not update your information. Please try again.`
      )
      if (process.env.WINIT_ENV !== 'test') {
        console.error('Error creating or sign up user ==>', err)
      }
      return false
    }

    // If we could not create a user. We can not upload ticket data.
    if (!authStore.isLoggedIn) return false
    const isTicketCreated=  await persistTrafficTickets(self)
    return isTicketCreated
  }
}

export default USER_INFO_CONFIGURATION
